/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import CollectionsBreadcrumbs from 'bundles/enterprise-collections/components/CollectionsBreadcrumbs';
import TailoredLearningPageContent from 'bundles/enterprise-collections/pages/TailoredLearningPageContent';
import CustomCoursesProvider from 'bundles/enterprise-collections/providers/CustomCoursesProvider';
import { COLLECTION_TYPE } from 'bundles/enterprise-collections/utils/SingleProgramConstants';
import AllBrowseCollections from 'bundles/enterprise-legacy-learner-home/components/single-program/AllBrowseCollections';
import AllCuratedCollections from 'bundles/enterprise-legacy-learner-home/components/single-program/AllCuratedCollections';
import type {
  AcademicDisciplineNameQueryVariables,
  AcademicDisciplineSummariesResult,
} from 'bundles/enterprise-legacy-learner-home/components/single-program/SingleProgramGraphqlQueries';
import { AcademicDisciplineNameQuery } from 'bundles/enterprise-legacy-learner-home/components/single-program/SingleProgramGraphqlQueries';
import { getCatalogCollectionProps } from 'bundles/enterprise-legacy-learner-home/components/single-program/withCatalogCollectionProps';
import type {
  EnterpriseProgramFromSlugFragment,
  NaptimeThirdPartyOrganizationFromSlugFragment,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/EnterpriseProgramBySlugQuery';
import type { OnProductCardClick } from 'bundles/enterprise-legacy-learner-home/types/Products';
import type { Product, ProductType } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import { getProductPath, redirectToUnifiedDescriptionPage } from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import type ProgramBrowsingExperienceV1 from 'bundles/naptimejs/resources/programBrowsingExperiences.v1';
import EnterpriseBadgeCollectionsProvider from 'bundles/page-config-common/providers/enterprise-skills/EnterpriseBadgeCollectionsProvider';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useBrowsingExperienceType, useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import EnterpriseAllSkillPathwaysDataProvider from 'bundles/unified-home-common/providers/enterprise/EnterpriseAllSkillPathwaysDataProvider';

export type PropsFromCaller = {
  programId: string;
  program: EnterpriseProgramFromSlugFragment;
  thirdPartyOrganizationId: string;
  thirdPartyOrganization: NaptimeThirdPartyOrganizationFromSlugFragment;
  isProgramMember: boolean;
  isProgramAdmin: boolean;
  isAuthenticatedUser: boolean;
  browsingExperienceType?: ProgramBrowsingExperienceV1;
  isBrowseOnlyProgram: boolean;
  shouldShowSponsoredByMessage: boolean;
  enableCurriculumBuilder: boolean;
  enableAcademicDisciplines?: boolean;
  allowOrgForCurriculumBuilder?: boolean;
  allowOrgForSpecializationConfiguration?: boolean;
  enableSkillsInSearchAndBrowse: boolean;
};

type CollectionsViewProps = {
  collectionType: COLLECTION_TYPE;
  programId: string;
  programSlug: string;
  programName: string;
  isAuthenticatedUser: boolean;
  thirdPartyOrganizationId: string;
  onProductCardClick: OnProductCardClick;
  enableCurriculumBuilder: boolean;
  academicDisciplineName?: string;
  allowOrgForSpecializationConfiguration?: boolean;
  catalogCollectionLength: number;
  thirdPartyOrgName: string;
};

export function CollectionsView({
  collectionType,
  programId,
  programSlug,
  programName,
  onProductCardClick,
  isAuthenticatedUser,
  catalogCollectionLength,
  thirdPartyOrganizationId,
  enableCurriculumBuilder,
  academicDisciplineName,
  allowOrgForSpecializationConfiguration,
  thirdPartyOrgName,
}: CollectionsViewProps) {
  const program = useProgram();
  switch (collectionType) {
    case COLLECTION_TYPE.CURATED:
      return (
        <EnterpriseBadgeCollectionsProvider programFromProps={program} limit={1}>
          <CustomCoursesProvider>
            <AllCuratedCollections
              programId={programId}
              programSlug={programSlug}
              programName={programName}
              onProductCardClick={onProductCardClick}
              limit={6}
              thirdPartyOrganizationName={thirdPartyOrgName}
              enableCurriculumBuilder={enableCurriculumBuilder}
              allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
              academicDisciplineName={academicDisciplineName}
              isAllCollectionsPage
            />
          </CustomCoursesProvider>
        </EnterpriseBadgeCollectionsProvider>
      );
    case COLLECTION_TYPE.BROWSE:
      return (
        <AllBrowseCollections
          programId={programId}
          programSlug={programSlug}
          isAuthenticatedUser={isAuthenticatedUser}
          onProductCardClick={onProductCardClick}
          limit={catalogCollectionLength}
          allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
        />
      );
    case COLLECTION_TYPE.TAILORED_LEARNING:
      return (
        <EnterpriseBadgeCollectionsProvider programFromProps={program}>
          <CustomCoursesProvider>
            <EnterpriseAllSkillPathwaysDataProvider ssr={false}>
              <TailoredLearningPageContent
                programSlug={programSlug}
                thirdPartyOrgName={thirdPartyOrgName}
                programName={programName}
              />
            </EnterpriseAllSkillPathwaysDataProvider>
          </CustomCoursesProvider>
        </EnterpriseBadgeCollectionsProvider>
      );
    default:
      return null;
  }
}

export function AllCollectionsPage(): JSX.Element | null {
  const router = useRouter();
  const program = useProgram();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const browsingExperienceType = useBrowsingExperienceType();

  const { allowOrgForCurriculumBuilder, enableAcademicDisciplines, allowOrgForSpecializationConfiguration } =
    useEnterpriseProductFeatures() || {};

  const {
    params: { programSlug },
  } = router;

  const isAuthenticatedUser = user.isAuthenticatedUser();

  const collectionType = router.params.collectionType as COLLECTION_TYPE;

  const { data } = useQuery<AcademicDisciplineSummariesResult, AcademicDisciplineNameQueryVariables>(
    AcademicDisciplineNameQuery,
    {
      skip: !program?.linkedAcademicDisciplineIds?.length,
      variables: {
        disciplineId: program?.linkedAcademicDisciplineIds?.[0] ?? '',
      },
      context: { clientName: 'gatewayGql' },
    }
  );

  const academicDisciplineName = data?.AcademicDiscipline?.queryAcademicDisciplineById?.name;

  const onProductCardClick = React.useCallback<
    (product: Product, productType: ProductType, collectionId?: string | null) => void
  >(
    (product, productType, collectionId) => {
      if (productType === 'clip') {
        window.open(`/videos/${product.slug}?program=${programSlug}`, '_blank');
      } else {
        redirectToUnifiedDescriptionPage(
          router,
          {
            slug: product.slug,
            productPagePath: getProductPath(product),
          },
          collectionId ?? undefined
        );
      }
    },
    [router, programSlug]
  );

  const { catalogCollectionLength } = getCatalogCollectionProps({
    program,
    browsingExperienceType,
    shouldHideCourseraRecommendations: false,
  });

  const styles = {
    container: css`
      background-color: var(--cds-color-neutral-background-primary);
    `,
    main: css`
      margin: 0 auto;
      padding: 0 12px;
    `,
  };

  if (!program || !thirdPartyOrganization) {
    return null;
  }

  return (
    <React.Fragment>
      <CollectionsBreadcrumbs
        collectionType={collectionType}
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        programSlug={programSlug}
        programName={program.name}
        thirdPartyOrgSlug={thirdPartyOrganization.slug}
        thirdPartyOrganizationId={thirdPartyOrganization.id}
      />
      <main css={styles.main}>
        <CollectionsView
          programId={program.id}
          // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          programSlug={programSlug}
          programName={program.name}
          collectionType={collectionType}
          isAuthenticatedUser={isAuthenticatedUser}
          onProductCardClick={onProductCardClick}
          catalogCollectionLength={catalogCollectionLength}
          thirdPartyOrganizationId={thirdPartyOrganization.id}
          thirdPartyOrgName={thirdPartyOrganization.name}
          enableCurriculumBuilder={Boolean(allowOrgForCurriculumBuilder)}
          academicDisciplineName={enableAcademicDisciplines ? academicDisciplineName : undefined}
          allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
        />
      </main>
    </React.Fragment>
  );
}

export default AllCollectionsPage;
